/**
 * PreviewHome - v2 shell.
 *
 * Owns the active-page state (driven by the sidebar) and renders the
 * matching page component. All page components live in this same
 * src/preview/v2/ directory.
 *
 * IMPORTANT: This namespace is reachable ONLY via ?preview=v2.
 * Do not import or wire it into the live app routes. Live untouched.
 */
import React, { useState, useEffect } from "react";
import { useViewport } from "./useViewport.v2";
import { tokensV2 as T } from "./tokens.v2";
import { Sidebar } from "./Sidebar.v2";
import { TopBar } from "./TopBar.v2";
import { HomePage } from "./HomePage";
import { FansPage } from "./FansPage";
import { CreatorsPage } from "./CreatorsPage";
import { BrandsPage } from "./BrandsPage";
import { MoneyPage } from "./MoneyPage";
import { OperationsPage } from "./OperationsPage";
import { AcquisitionPage } from "./AcquisitionPage";
import { AuditPage } from "./AuditPage";
import { InvestorPage } from "./InvestorPage";
import { RelationalReportsPage } from "./RelationalReportsPage";
import { ReportBuilder } from "./ReportBuilder.v2";
import { AIAssistant } from "../../features/ai/AIAssistant";
import { CustomDashboardPage } from "./CustomDashboardPage";
import { SettingsAccessPage } from "./SettingsAccessPage";
import { MetricDetail } from "./MetricDetail.v2";
import { resolveRange } from "./dateRange.v2";
import { resolvePrevRange } from "./prevRange.v2";
import { usePageRoute } from "./route.v2";

// Page registry. Reports / Templates / History / Settings were left as design
// mocks during v2 buildout — they're hidden from the live dashboard until each
// page is wired to a real backend.
const PAGES = {
  home:        { title: "Home",            render: (set, ctx) => <HomePage range={ctx?.range} rangeId={ctx?.rangeId} onSelectSection={set} onOpenMetric={ctx?.openMetric} /> },
  fans:        { title: "Fans",            render: (set, ctx) => <FansPage range={ctx?.range} rangeId={ctx?.rangeId} onOpenMetric={ctx?.openMetric} /> },
  creators:    { title: "Creators",        render: (set, ctx) => <CreatorsPage range={ctx?.range} rangeId={ctx?.rangeId} /> },
  brands:      { title: "Brands",          render: (set, ctx) => <BrandsPage range={ctx?.range} rangeId={ctx?.rangeId} /> },
  money:       { title: "Money",           render: (set, ctx) => <MoneyPage range={ctx?.range} rangeId={ctx?.rangeId} /> },
  operations:  { title: "Operations",      render: (set, ctx) => <OperationsPage range={ctx?.range} rangeId={ctx?.rangeId} /> },
  acquisition: { title: "Acquisition",     render: (set, ctx) => <AcquisitionPage range={ctx?.range} rangeId={ctx?.rangeId} /> },
  audit:       { title: "Audit & Tax",     render: (set, ctx) => <AuditPage range={ctx?.range} rangeId={ctx?.rangeId} /> },
  investor:    { title: "Investor data",        render: (set, ctx) => <InvestorPage range={ctx?.range} rangeId={ctx?.rangeId} onOpenMetric={ctx?.openMetric} /> },
  relational:  { title: "Relational reports",   render: ()    => <RelationalReportsPage /> },
  custom:      { title: "Build your own",       render: (set, ctx) => <CustomDashboardPage authToken={ctx?.authToken} /> },
  settings:    { title: "Settings & access",    render: (set, ctx) => <SettingsAccessPage authToken={ctx?.authToken} authUser={ctx?.authUser} /> },
};

// Pages list passed to TopBar's nav search
const PAGE_LIST = Object.entries(PAGES).map(([id, p]) => ({ id, title: p.title }));

function deriveUserPill(authUser) {
  if (!authUser) return { initials: 'JT', name: 'Jasmin T', role: 'CEO' };
  const name = authUser.name || authUser.username || 'User';
  const parts = name.trim().split(/\s+/);
  const initials = (parts[0]?.[0] || '?') + (parts[1]?.[0] || '');
  const role = (authUser.role || authUser.primary_role || 'user').toString().toUpperCase();
  return { initials: initials.toUpperCase(), name, role };
}

export function PreviewHome({ onLogout = null, authUser = null, authToken = null } = {}) {
  // Each page has its own URL (/, /money, /creators, …) so bookmarking,
  // sharing, refresh, and browser back/forward all work per page.
  const { active, navigate } = usePageRoute();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  // usePageRoute keeps the PAGE in the URL. The date range is NOT in the URL, so
  // persist it (and any custom range) in localStorage so a refresh keeps it too.
  const [rangeId, setRangeId] = useState(() => {
    try { return localStorage.getItem("fc_range_id") || "today"; } catch (_) { return "today"; }
  });
  const [customRange, setCustomRange] = useState(() => {
    try { const v = localStorage.getItem("fc_custom_range"); return v ? JSON.parse(v) : null; } catch (_) { return null; }
  });
  useEffect(() => { try { localStorage.setItem("fc_range_id", rangeId); } catch (_) {} }, [rangeId]);
  useEffect(() => {
    try {
      if (customRange) localStorage.setItem("fc_custom_range", JSON.stringify(customRange));
      else localStorage.removeItem("fc_custom_range");
    } catch (_) {}
  }, [customRange]);
  const [syncedAt, setSyncedAt] = useState(() => Date.now());
  const [builderOpen, setBuilderOpen] = useState(false);
  const [openMetric, setOpenMetric] = useState(null); // { id, live? }
  const { isMobile } = useViewport();
  const page = PAGES[active] || PAGES.home;

  const handleSelect = (id) => {
    navigate(id);
    setSyncedAt(Date.now());
    if (isMobile) setDrawerOpen(false);
  };

  const handleMenuClick = () => {
    if (isMobile) setDrawerOpen(true);
    else setDesktopOpen((v) => !v);
  };

  const handleNavigate = (id) => {
    if (id === "__new_report__") { setBuilderOpen(true); return; }
    handleSelect(id);
  };

  const sidebarVisible = isMobile ? drawerOpen : desktopOpen;

  return (
    <div style={{ display: "flex", minHeight: "100vh", backgroundColor: T.surface.canvas, color: T.text.primary, fontFamily: T.font.sans }}>
      {sidebarVisible && (
        <Sidebar
          active={active}
          onSelect={handleSelect}
          isMobile={isMobile}
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      )}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <TopBar
          pageTitle={page.title}
          rangeId={rangeId}
          customRange={customRange}
          onRangeChange={(id, range) => { setRangeId(id); setCustomRange(range); setSyncedAt(Date.now()); }}
          resolvedRange={resolveRange(rangeId, customRange)}
          prevRange={resolvePrevRange(rangeId, resolveRange(rangeId, customRange))}
          isMobile={isMobile}
          onMenuClick={handleMenuClick}
          pages={PAGE_LIST}
          onNavigate={handleNavigate}
          syncedAt={syncedAt}
          user={deriveUserPill(authUser)}
          onLogout={onLogout}
        />
        <main style={{
          flex: 1,
          padding: isMobile ? "14px 14px 32px" : "20px 28px 48px",
          maxWidth: 1360, width: "100%", margin: "0 auto", boxSizing: "border-box",
        }}>
          {page.render(handleSelect, {
            // Tiles pass `live` so the modal can render real numbers + the
            // current pill window without a separate fetch. `live` is optional;
            // omit it for tiles that don't have a live value.
            openMetric: (id, live) => setOpenMetric({ id, live: live || null }),
            range: resolveRange(rangeId, customRange),
            rangeId,
            // Threaded for the Build-your-own dashboard + Settings pages so
            // they can call authenticated endpoints (/ai/ask, /auth/admin/*).
            authToken,
            authUser,
          })}
        </main>
      </div>
      {builderOpen && (
        <ReportBuilder
          onClose={() => setBuilderOpen(false)}
          initialRangeId={rangeId}
          initialCustomRange={customRange}
        />
      )}
      {/* AI assistant — floating action button (bottom-right). Self-contained
          tree, gated on having an auth token so it never renders before login.
          Backend: /api/v1/ai/ask (rate-limited 5/min + 30/hr). */}
      {authToken && <AIAssistant token={authToken} />}
      {openMetric && (
        <MetricDetail
          metricId={openMetric.id}
          live={openMetric.live}
          rangeId={rangeId}
          range={resolveRange(rangeId, customRange)}
          onClose={() => setOpenMetric(null)}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}
